/**
 * Meta Ads Performance dashboard page.
 * Passes Meta-specific config to the generic PlatformDashboard.
 * Meta uses "adset" terminology (vs TikTok's "adgroup").
 */
import React from 'react';
import { useTranslation } from 'src/arb-wrapper';
import type { PaidMediaInsightsConfig } from 'src/components/paid-media-insights/InsightsSection';
import PlatformDashboard from 'src/components/paid-media-dashboard/PlatformDashboard';
import { formatPercent } from 'src/utils/dashboardUtils';

const META_TABS = [
  { value: 'campaign', label: 'Campaign' },
  { value: 'adset', label: 'Ad Set' },
  { value: 'ad', label: 'Ad' },
  { value: 'product', label: 'Product' },
];

const META_BREAKDOWN_OPTIONS = [
  { value: 'age_gender', label: 'Demographics Breakdown' },
];

const META_COLUMN_WIDTHS_OVERRIDES: Record<string, number> = {
  campaign_name: 600,
  adset_name: 600,
  adgroup_name: 600,
  ad_name: 600,
  product_id: 300,
  gtis: 600,
  titles: 600,
  en_titles: 600,
  reftags: 600,
};

const META_FORMAT_OVERRIDES = {
  ctr: (value: unknown) => formatPercent(value),
};

const META_SUMMARY_COLUMNS = [
  'campaign_name', 'adset_name', 'adgroup_name', 'ad_name', 'account_name', 'advertiser_name', 'objective', 'age', 'gender',
  'product_id', 'gtis', 'titles', 'en_titles', 'reftags', 'account_currency', 'spend', 'impressions', 'clicks', 'ctr', 'cpc', 'cpm',
  'reach', 'frequency', 'video_p100_watched_actions_video_view',
  'actions_like', 'actions_comment', 'actions_post',
];

const META_ALWAYS_VISIBLE_KEYS = [
  'campaign_name',
  'adset_name',
  'ad_name',
  'product_id',
  'titles',
];

const META_CURRENCY_COL_KEY = 'account_currency';

const META_INSIGHTS_CONFIG = {
  cardMetrics: [
    {
      key: 'spend',
      label: 'paid_media_insights_metric_spend',
      numberFormat: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: 'currency',
      },
    },
    {
      key: 'impressions',
      label: 'paid_media_insights_metric_impressions',
      numberFormat: { maximumFractionDigits: 0, style: 'decimal' },
    },
    {
      key: 'clicks',
      label: 'paid_media_insights_metric_clicks',
      numberFormat: { maximumFractionDigits: 0, style: 'decimal' },
    },
    {
      key: 'ctr',
      label: 'paid_media_insights_metric_ctr',
      numberFormat: { maximumFractionDigits: 2, style: 'percent' },
    },
  ],
  engagementMetrics: [
    { key: 'actions_like', label: 'paid_media_insights_metric_likes' },
    { key: 'actions_comment', label: 'paid_media_insights_metric_comments' },
    { key: 'actions_post', label: 'paid_media_insights_metric_shares' },
  ],
} satisfies PaidMediaInsightsConfig;

const MetaDashboard: React.FC = () => {
  const { t } = useTranslation();

  const columnTooltips: Record<string, string> = {
    campaign_name: t('paid_media_tooltip_campaign_name'),
    account_name: t('paid_media_tooltip_meta_account_name'),
    objective: t('paid_media_tooltip_meta_objective'),
    adset_name: t('paid_media_tooltip_meta_adset_name'),
    ad_name: t('paid_media_tooltip_ad_name'),
    account_currency: t('paid_media_tooltip_meta_account_currency'),
    spend: t('paid_media_tooltip_spend'),
    impressions: t('paid_media_tooltip_impressions'),
    clicks: t('paid_media_tooltip_clicks'),
    ctr: t('paid_media_tooltip_ctr'),
    cpc: t('paid_media_tooltip_cpc'),
    cpm: t('paid_media_tooltip_cpm'),
    reach: t('paid_media_tooltip_reach'),
    frequency: t('paid_media_tooltip_frequency'),
    outbound_clicks_outbound_click: t('paid_media_tooltip_meta_outbound_clicks_outbound_click'),
    inline_post_engagement: t('paid_media_tooltip_meta_inline_post_engagement'),
    actions_landing_page_view: t('paid_media_tooltip_meta_actions_landing_page_view'),
    actions_like: t('paid_media_tooltip_meta_actions_like'),
    actions_comment: t('paid_media_tooltip_meta_actions_comment'),
    actions_post: t('paid_media_tooltip_meta_actions_post'),
    video_play_actions_video_view: t('paid_media_tooltip_meta_video_play_actions_video_view'),
    video_continuous_2_sec_watched_actions_video_view: t('paid_media_tooltip_meta_video_continuous_2_sec_watched_actions_video_view'),
    video_30_sec_watched_actions_video_view: t('paid_media_tooltip_meta_video_30_sec_watched_actions_video_view'),
    video_p25_watched_actions_video_view: t('paid_media_tooltip_meta_video_p25_watched_actions_video_view'),
    video_p50_watched_actions_video_view: t('paid_media_tooltip_meta_video_p50_watched_actions_video_view'),
    video_p75_watched_actions_video_view: t('paid_media_tooltip_meta_video_p75_watched_actions_video_view'),
    video_p100_watched_actions_video_view: t('paid_media_tooltip_meta_video_p100_watched_actions_video_view'),
    cost_per_thruplay_video_view: t('paid_media_tooltip_meta_cost_per_thruplay_video_view'),
  };

  return (
    <PlatformDashboard
      alwaysVisibleKeys={META_ALWAYS_VISIBLE_KEYS}
      breakdownExcludedTabs={['product']}
      breakdownOptions={META_BREAKDOWN_OPTIONS}
      columnTooltips={columnTooltips}
      columnWidths={META_COLUMN_WIDTHS_OVERRIDES}
      currencyColumnKey={META_CURRENCY_COL_KEY}
      filterLabelOverrides={{ advertiserId: 'Account' }}
      formatOverrides={META_FORMAT_OVERRIDES}
      insightsConfig={META_INSIGHTS_CONFIG}
      summaryColumns={META_SUMMARY_COLUMNS}
      tabs={META_TABS}
      title="Meta Ads Performance"
      titleSearchSubViews={['product']}
      view="meta"
    />
  );
};

export default MetaDashboard;
